// Helpers para rangos de fecha preset (Hoy, 7 días, Mes actual, Mes anterior).
// Devuelven strings YYYY-MM-DD compatibles con <input type="date">.
//
// IMPORTANTE: todos los rangos se calculan en zona Montevideo (UTC-3) para que
// los reportes sean consistentes sin importar la zona del navegador del usuario.
// Sin esto, un cierre hecho a las 23:30 UYT del último día del mes podía caer
// fuera del "mes actual" si el admin lo abría desde Europa.

const TZ = 'America/Montevideo';

export const PRESETS = [
  { key: 'today', label: 'Hoy' },
  { key: '7d', label: '7 días' },
  { key: 'month', label: 'Mes actual' },
  { key: 'lastMonth', label: 'Mes anterior' },
  { key: 'custom', label: 'Personalizado' },
];

const pad = (n) => String(n).padStart(2, '0');

// Devuelve { year, month, day } (números) según la zona Montevideo
// para un Date dado. Esta es la única forma confiable de saber "qué día es
// hoy en Uruguay" sin depender del reloj del navegador.
function partsInMVD(date = new Date()) {
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  const parts = Object.fromEntries(
    fmt.formatToParts(date).map((p) => [p.type, p.value])
  );
  return {
    year: parseInt(parts.year, 10),
    month: parseInt(parts.month, 10),
    day: parseInt(parts.day, 10),
  };
}

function toYmd({ year, month, day }) {
  return `${year}-${pad(month)}-${pad(day)}`;
}

// Último día del mes (28-31). El número del día NO depende de la zona horaria,
// así que usar `new Date(y, m, 0)` es seguro acá aunque no esté en MVD.
function lastDayOfMonth(year, month) {
  return new Date(year, month, 0).getDate();
}

// Fecha de hoy en formato YYYY-MM-DD según Montevideo (no según el navegador).
export function todayYmdMVD(now = new Date()) {
  return toYmd(partsInMVD(now));
}

// Date object representando "00:00:00 hora Montevideo" del YYYY-MM-DD dado.
// Internamente es UTC, listo para comparar contra entry.fecha que viene UTC.
// Ej: startOfDayMVD('2026-04-29') → 2026-04-29T03:00:00.000Z
export function startOfDayMVD(ymd) {
  return new Date(`${ymd}T00:00:00-03:00`);
}

// Date object representando "23:59:59.999 hora Montevideo" del YYYY-MM-DD dado.
// Ej: endOfDayMVD('2026-04-29') → 2026-04-30T02:59:59.999Z
export function endOfDayMVD(ymd) {
  return new Date(`${ymd}T23:59:59.999-03:00`);
}

export function getPresetRange(key, now = new Date()) {
  const today = partsInMVD(now);
  const todayYmd = toYmd(today);

  switch (key) {
    case 'today':
      return { start: todayYmd, end: todayYmd };

    case '7d': {
      // 6 días hacia atrás desde el "hoy" uruguayo.
      const todayStart = startOfDayMVD(todayYmd);
      const sixDaysAgo = new Date(todayStart.getTime() - 6 * 24 * 60 * 60 * 1000);
      const startYmd = toYmd(partsInMVD(sixDaysAgo));
      return { start: startYmd, end: todayYmd };
    }

    case 'month': {
      const startYmd = toYmd({ year: today.year, month: today.month, day: 1 });
      const endDay = lastDayOfMonth(today.year, today.month);
      const endYmd = toYmd({ year: today.year, month: today.month, day: endDay });
      return { start: startYmd, end: endYmd };
    }

    case 'lastMonth': {
      let lmYear = today.year;
      let lmMonth = today.month - 1;
      if (lmMonth === 0) {
        lmYear -= 1;
        lmMonth = 12;
      }
      const startYmd = toYmd({ year: lmYear, month: lmMonth, day: 1 });
      const endDay = lastDayOfMonth(lmYear, lmMonth);
      const endYmd = toYmd({ year: lmYear, month: lmMonth, day: endDay });
      return { start: startYmd, end: endYmd };
    }

    case 'custom':
    default:
      return null; // Custom no fuerza un rango, el usuario elige
  }
}
